import { Button } from '@invent/wl-ui-kit-next/ui';
import { Link, Navigate } from 'react-router-dom';

import { TEAM_PATHS } from '@/domains/admin';
import { AuthShell, AuthSplash, DoorHeading, useAuth } from '@/domains/auth';
import { PORTAL_PATHS } from '@/domains/survey';

/**
 * Where the two portals meet — the role landing and the two refusals (the two-portals design, §2).
 *
 * **This file and the demo guide are the only places a link crosses from one portal to the
 * other.** Neither shell imports it, and neither shell links into the other; what crosses does so
 * here, in the front door's frame, which belongs to neither.
 *
 * All three decide what to render and nothing more. The access control is survey-api's policies,
 * which refuse an advisor 403 on every Team Portal read before any lookup.
 */

/** `/` — one sign-in, and the account's kind decides where it lands (SR-228): never a role, since
 *  a Team Portal account holding none still works in the Team Portal. */
export function RoleLanding() {
  const { user } = useAuth();
  if (!user) return <AuthSplash />;
  return (
    <Navigate to={user.kind === 'advisor' ? PORTAL_PATHS.home : TEAM_PATHS.home} replace />
  );
}

/**
 * An advisor at a Team Portal address. A page that says so rather than the silent bounce home the
 * admin section used, which `AdminRoute` justified as "not theirs to know about" — the brief asked
 * for a refusal, and the Team Portal's existence is no secret. No part of the Team Portal's shell
 * renders, so nothing of its navigation leaks; the one link goes to the viewer's own portal.
 */
export function TeamBoundary() {
  return (
    <AuthShell title="Team Portal" shape="page" zoneName={null}>
      <DoorHeading
        title="This is Veritant’s Team Portal"
        lede={
          'It is where Veritant’s research, compliance and data staff work. ' +
          'Your own surveys are in the Advisor Portal.'
        }
      />
      <Button asChild>
        <Link to={PORTAL_PATHS.home}>Go to your Advisor Portal</Link>
      </Button>
    </AuthShell>
  );
}

/**
 * A Team Portal account at an Advisor Portal address — never a silently empty advisor home.
 * Staff never fill in a survey here, even an account that acts for participants (ruled
 * 2026-09-28); they read responses in the Team Portal.
 */
export function PortalBoundary() {
  return (
    <AuthShell title="Advisor Portal" shape="page" zoneName={null}>
      <DoorHeading
        title="This is the advisor portal"
        lede={
          'It is where advisors, teams and firms fill in their surveys. Veritant staff read ' +
          'every response in the Team Portal, which is where your account works.'
        }
      />
      <Button asChild>
        <Link to={TEAM_PATHS.home}>Go to the Team Portal</Link>
      </Button>
    </AuthShell>
  );
}
